/**
 * announcementPhase — when to show a planned-outage notice, and how loudly.
 *
 * The server deliberately does NOT tell us this. It hands back the raw
 * timestamps and we derive the phase here, on every render, from the client's
 * own clock. That matters for two reasons:
 *
 *   1. A server-computed phase is stale the moment it is serialized. This is
 *      a feature whose entire job is to be right about time — a banner that
 *      still says "starts in 45 minutes" twenty minutes later is worse than
 *      no banner, because people plan around it.
 *   2. It keeps working offline. `useAnnouncements` caches the active set in
 *      localStorage, so a window that was announced while the API was up
 *      still escalates correctly once the API goes down — which is precisely
 *      the moment the user needs to see it.
 *
 * Pure functions in utils/ (no hooks, no fetch), per the repo convention, so
 * the time arithmetic can be tested without mounting anything.
 */

/**
 * How close to the window counts as "imminent" — the point at which we stop
 * being informational and start telling people to save their work.
 *
 * An hour is the honest number for this app: a landman part-way through a
 * deal form needs enough warning to finish or abandon it, and much more than
 * an hour of red banner is just wallpaper people stop seeing.
 */
export const IMMINENT_MS = 60 * 60 * 1000;

/** Phases the server will accept as a `dismissed_phase`. */
export const PHASES = ["upcoming", "imminent", "active"];

const ms = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * Which phase an announcement is in right now.
 *
 * @returns {"scheduled"|"upcoming"|"imminent"|"active"|"ended"}
 *   - `scheduled` — published but `publish_at` hasn't arrived. Not shown.
 *   - `upcoming`  — showing, window is more than IMMINENT_MS away.
 *   - `imminent`  — showing, window starts within IMMINENT_MS.
 *   - `active`    — the window is in progress. Also the phase for a notice
 *                   with NO window at all (a plain 'info' announcement is
 *                   simply in effect; there is nothing "upcoming" about it).
 *   - `ended`     — `ends_at` has passed. Not shown.
 */
export const announcementPhase = (announcement, now = Date.now()) => {
  if (!announcement) return "ended";

  const publishAt = ms(announcement.publish_at);
  const startsAt = ms(announcement.starts_at);
  const endsAt = ms(announcement.ends_at);

  if (publishAt !== null && now < publishAt) return "scheduled";
  if (endsAt !== null && now >= endsAt) return "ended";

  // No window: an info notice is simply in effect while it is being shown.
  if (startsAt === null) return "active";

  if (now >= startsAt) return "active";
  return startsAt - now <= IMMINENT_MS ? "imminent" : "upcoming";
};

/** Is this announcement in a phase where it should appear at all? */
export const isVisible = (announcement, now = Date.now()) =>
  PHASES.includes(announcementPhase(announcement, now));

/**
 * Should the BANNER be rendered?
 *
 * Dismissal is per-phase, not permanent. Someone who waves away "maintenance
 * on Saturday" on Tuesday has said "not now" about the Tuesday version of
 * that message — not about the one at 4:55pm on Saturday when the window is
 * minutes away. So a dismissal only suppresses the phase it was made in, and
 * each escalation earns one fresh chance to be seen.
 */
export const shouldShowBanner = (announcement, now = Date.now()) => {
  const phase = announcementPhase(announcement, now);
  if (!PHASES.includes(phase)) return false;
  return announcement?.dismissed_phase !== phase;
};

/**
 * Should the MODAL be rendered?
 *
 * Only for announcements explicitly flagged `require_ack`, and only until the
 * user closes it once. Unlike the banner this acknowledgement is permanent —
 * a modal that comes back is the fastest way to teach someone to dismiss it
 * without reading, which defeats the one job it has.
 */
export const shouldShowModal = (announcement, now = Date.now()) => {
  if (!announcement?.require_ack) return false;
  if (announcement.acknowledged) return false;
  return isVisible(announcement, now);
};

/**
 * The announcement that most deserves the user's attention.
 *
 * Ordered by phase first (an outage starting in ten minutes outranks one next
 * week, whatever their severities), then by severity within a phase. We show
 * one banner rather than a stack: two banners is a wall people scroll past,
 * and the second-most-urgent outage notice has never been the one that
 * mattered.
 */
const PHASE_RANK = { active: 0, imminent: 1, upcoming: 2 };
const SEVERITY_RANK = { critical: 0, warning: 1, info: 2 };

export const mostUrgent = (announcements, now = Date.now()) => {
  const visible = (announcements || []).filter((a) => shouldShowBanner(a, now));
  if (!visible.length) return null;
  return visible.slice().sort((a, b) => {
    const pa = PHASE_RANK[announcementPhase(a, now)] ?? 9;
    const pb = PHASE_RANK[announcementPhase(b, now)] ?? 9;
    if (pa !== pb) return pa - pb;
    const sa = SEVERITY_RANK[a.severity] ?? 9;
    const sb = SEVERITY_RANK[b.severity] ?? 9;
    if (sa !== sb) return sa - sb;
    return (ms(a.starts_at) ?? Infinity) - (ms(b.starts_at) ?? Infinity);
  })[0];
};

/**
 * Human phrasing for how long until (or how long left of) the window.
 *
 * Deliberately coarse. "Starts in about 2 hours" is more useful than "in 1h
 * 53m" — nobody schedules their afternoon to the minute, and a countdown
 * ticking in the corner of a land-acquisition app is a distraction. Under an
 * hour we do give minutes, because that is when precision starts to matter.
 */
export const describeTiming = (announcement, now = Date.now()) => {
  const phase = announcementPhase(announcement, now);
  const startsAt = ms(announcement?.starts_at);
  const endsAt = ms(announcement?.ends_at);

  // No start means there is no outage window, whatever `ends_at` says — the
  // end date on a windowless notice is an auto-expiry, not a return-to-service
  // time. Without this guard an informational notice set to disappear on
  // Friday reads "In progress — expected back Fri 5:00 PM".
  if (startsAt === null) return null;

  if (phase === "active") {
    if (endsAt === null) return null;
    return `In progress — expected back ${formatClock(endsAt)}`;
  }

  const delta = startsAt - now;
  const mins = Math.round(delta / 60000);
  if (mins <= 1) return "Starting now";
  if (mins < 60) return `Starts in ${mins} minutes`;
  const hours = Math.round(delta / 3600000);
  if (hours < 24) {
    return `Starts in about ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = Math.round(delta / 86400000);
  return `Starts in ${days} day${days === 1 ? "" : "s"} — ${formatClock(startsAt)}`;
};

/**
 * Format an instant in the READER's local zone.
 *
 * Always local, never the author's zone: an admin in Oklahoma writing "8pm"
 * and a broker reading it somewhere else must not be looking at the same
 * string and picturing different hours.
 */
export const formatClock = (value) => {
  const t = typeof value === "number" ? value : ms(value);
  if (t === null) return "";
  return new Date(t).toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default {
  IMMINENT_MS,
  PHASES,
  announcementPhase,
  isVisible,
  shouldShowBanner,
  shouldShowModal,
  mostUrgent,
  describeTiming,
  formatClock,
};
